import React, { useRef } from 'react'
import produce from 'immer'

import {
  Button,
  FlatButton,
  Modal,
  ModalInjectedProps,
  Text,
} from '@sketch/components'

import {
  InvitePeopleField,
  errorPreventiveCacheRead,
  removeFromPaginated,
} from '@sketch/modules-common'
import { useToast } from '@sketch/toasts'

import {
  GetWorkspacePermissionGroupMembersDocument,
  GetWorkspacePermissionGroupMembersQuery,
  GetWorkspacePermissionGroupMembersQueryVariables,
  useAddMemberToPermissionGroupMutation,
  useRemovePermissionGroupMemberMutation,
} from '@sketch/gql-types'

import { useMembersAutoComplete } from '../../hooks'
import PermissionGroupMemberTable from '../../components/PermissionGroupMemberTable'
import PermissionMemberInputWarning from '../../components/PermissionMemberInputWarning'

import {
  AddMemberButton,
  InputWrapper,
  LoadingPlaceholder,
} from './PermissionGroupMembersModal.styles'

interface RemoveMemberProps {
  memberIdentifier: string
}

const RemoveGroupMember = (props: RemoveMemberProps) => {
  const { memberIdentifier } = props
  const { showToast } = useToast()

  const [removeMember, { loading }] = useRemovePermissionGroupMemberMutation({
    onError: 'show-toast',
    onCompleted: () => {
      showToast('Member has been removed', 'positive')
    },
    update: cache => {
      removeFromPaginated(
        cache,
        {
          __typename: 'PermissionGroupMembership',
          identifier: memberIdentifier,
        },
        () => true
      )
    },
  })

  if (loading) {
    return <LoadingPlaceholder />
  }

  return (
    <FlatButton
      variant="negative"
      onClick={() => {
        removeMember({
          variables: {
            input: {
              member: memberIdentifier,
            },
          },
        })
      }}
    >
      Remove
    </FlatButton>
  )
}

interface PermissionGroupMemberModalProps extends ModalInjectedProps {
  permissionGroupIdentifier: string
  permissionGroupName: string
  workspaceIdentifier: string
}

const PermissionGroupMembersModal = (
  props: PermissionGroupMemberModalProps
) => {
  const {
    hideModal,
    permissionGroupIdentifier,
    permissionGroupName,
    workspaceIdentifier,
  } = props

  const { showToast } = useToast()
  const containerRef = useRef<HTMLInputElement>(null)

  const autoComplete = useMembersAutoComplete({
    workspaceIdentifier,
    permissionGroupIdentifier,
    excludedMemberIdentifiers: [],
  })

  const [
    addMember,
    { loading: addMemberLoading },
  ] = useAddMemberToPermissionGroupMutation({
    onError: 'show-toast',
    onCompleted: () => {
      showToast('Member has been added', 'positive')
      autoComplete.combobox.reset()
    },
    update: (cache, { data }) => {
      const cacheResult = errorPreventiveCacheRead<
        GetWorkspacePermissionGroupMembersQuery,
        GetWorkspacePermissionGroupMembersQueryVariables
      >(cache, {
        query: GetWorkspacePermissionGroupMembersDocument,
        variables: { permissionGroupIdentifier },
      })

      if (!cacheResult) {
        return
      }

      const updatedCacheResult = produce(cacheResult, result => {
        data!.addPermissionGroupMembers.member.forEach(member => {
          result.permissionGroup.members.entries.push(member!)
          result.permissionGroup.members.meta.totalCount++
        })
      })

      cache.writeQuery<
        GetWorkspacePermissionGroupMembersQuery,
        GetWorkspacePermissionGroupMembersQueryVariables
      >({
        query: GetWorkspacePermissionGroupMembersDocument,
        variables: { permissionGroupIdentifier },
        data: updatedCacheResult,
      })
    },
  })

  return (
    <Modal onCancel={hideModal}>
      <Modal.Header>View and Edit Group Members</Modal.Header>
      <Modal.Body>
        <Text>
          View, add or search for Members in the <b>“{permissionGroupName}”</b>{' '}
          group.
        </Text>
        <InputWrapper>
          <InvitePeopleField
            label="Users List"
            placeholder="Add a name or email address"
            containerRef={containerRef}
            autocomplete={{ ...autoComplete, formik: {} as any }}
            dynamicAction={() => <PermissionMemberInputWarning />}
          />
          <AddMemberButton
            variant="primary"
            onClick={() => {
              addMember({
                variables: {
                  input: {
                    identifier: permissionGroupIdentifier,
                    members: [autoComplete.selectedItem!.identifier],
                  },
                },
              })
            }}
            loading={addMemberLoading}
            disabled={!autoComplete.selectedItem}
          >
            Add
          </AddMemberButton>
        </InputWrapper>

        <PermissionGroupMemberTable
          permissionGroupIdentifier={permissionGroupIdentifier}
          renderAction={identifier => (
            <RemoveGroupMember memberIdentifier={identifier} />
          )}
        />
      </Modal.Body>
      <Modal.Footer>
        <Button onClick={hideModal}>Done</Button>
      </Modal.Footer>
    </Modal>
  )
}

export default PermissionGroupMembersModal
